import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard,
  Users,
  FileText,
  Printer,
  Package,
  Gauge,
  Receipt,
  Ticket,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Menu,
  X,
  Activity,
  Clock,
  Inbox,
  Library,
  CreditCard,
  TrendingUp,
  Building2,
  Plus,
  UserCheck,
  FileSignature,
  Landmark,
  AlertTriangle,
  ShoppingCart,
  ListChecks,
  GraduationCap,
  BarChart3,
  FileBarChart2,
  Award,
  Store,
  Target,
  Phone,
  Monitor,
  Shield,
} from 'lucide-react';
import { Button } from './ui/button';
import NotificationCenter from './NotificationCenter';
import { settingsAPI, ticketsAPI } from '../lib/api';
import { isEntryVisible, isMenuItemVisible } from '../lib/menuVisibility';

const LOGO_URL = "/logo-ldws.png";

// Menu Gestion — full operational sidebar
const menuItemsGestion = [
  { path: '/dashboard', label: 'Tableau de bord', icon: LayoutDashboard },
  // Flux d'activité — RÉSERVÉ AU PROPRIÉTAIRE (via `ownerOnly`, honoré par le filtre plus bas).
  { path: '/commercial/flux-activite', label: "Flux d'activité", icon: Activity, ownerOnly: true },
  { path: '/commandes', label: 'Commandes', icon: ShoppingCart },
  { path: '/clients', label: 'Clients', icon: Users },
  { path: '/contrats', label: 'Contrats', icon: FileText },
  { path: '/suivi-contrat', label: 'Suivi contrat', icon: FileSignature },
  { path: '/financements', label: 'Financements', icon: Landmark },
  { path: '/equipements', label: 'Équipements', icon: Printer },
  { path: '/abonnements', label: 'Abonnements', icon: Package },
  // E-commerce — tout ce qui touche au site public (vitrine). Un seul lien : la page d'ensemble liste les sous-services
  // (demandes de devis, articles, et les suivants) et chacun s'ouvre depuis sa carte. Un nouveau service s'ajoute à la
  // page d'ensemble (`SERVICES`), pas ici. Visible avec storefront:read, jamais pour un lien qui aboutirait à un 403.
  { path: '/vitrine', label: 'E-commerce', icon: Store, permission: 'storefront:read' },
  {
    id: 'facturation',
    label: 'Facturation',
    icon: Receipt,
    submenu: {
      title: 'Facturation',
      items: [
        // Facturation récurrente segmentée par activité : chaque lien ouvre la feuille
        // de génération de SON activité (onglet piloté par ?tab=). Ordre métier demandé.
        { heading: 'Facturation récurrente' },
        { path: '/facturation?tab=worksheet', tab: 'worksheet', label: 'Impression', icon: Printer },
        { path: '/facturation?tab=telephonie', tab: 'telephonie', label: 'Télécom', icon: Phone },
        { path: '/facturation?tab=informatique', tab: 'informatique', label: 'Informatique', icon: Monitor },
        { path: '/facturation?tab=securite', tab: 'securite', label: 'Sécurité', icon: Shield },
        { path: '/facturation?tab=mobilier', tab: 'mobilier', label: 'Mobilier', icon: Package },
        { heading: 'Suivi' },
        { path: '/facturation/liste-factures', label: 'Liste des factures', icon: FileText },
        { path: '/facturation/rentabilite', label: 'Rentabilité', icon: TrendingUp },
        { path: '/compteurs', label: 'Compteurs', icon: Gauge },
        { path: '/demandes-compteurs', label: 'Demandes relevés', icon: Inbox },
        { path: '/recovery', label: 'Suivi des Impayés', icon: AlertTriangle },
        { path: '/mandats-sepa', label: 'Gestion des mandats SEPA', icon: FileSignature },
        { path: '/prelevements-sepa', label: 'Prélèvements SEPA', icon: CreditCard },
      ]
    }
  },
  { path: '/tickets', label: 'Tickets', icon: Ticket },
  { path: '/espace-client-gestion', label: 'Espace Client', icon: UserCheck },
  // K-PAX et Sync HubSpot déplacés dans Paramètres ; Formation retirée de Gestion.
];

// Menu Commerce — dedicated commercial views (no gestion mirrors).
// Chaque entrée déclare la permission requise (`anyPermission` = au moins une)
// afin qu'un utilisateur au droit commercial partiel ne voie que les liens qu'il
// peut réellement ouvrir (sinon 403). Une entrée sans permission est toujours
// visible dès que le menu Commerce est accessible.
const menuItemsCommerce = [
  { path: '/commercial/dashboard', label: 'Mon tableau de bord', icon: BarChart3, permission: 'commercial:read' },
  {
    id: 'pilotage-commercial',
    label: 'Pilotage commercial',
    icon: Activity,
    permission: 'commercial:read',
    submenu: {
      title: 'Pilotage commercial',
      items: [
        { path: '/commercial/analyse-activite', label: 'Analyse des prospects', icon: Target, permission: 'commercial:read' },
        { path: '/commercial/prospection', label: 'Production commerciale', icon: Activity, permission: 'commercial:read' },
      ],
    },
  },
  { path: '/commercial/clients', label: 'Parc client', icon: Users, permission: 'commercial:read' },
  { path: '/commercial/commissions', label: 'Commissions', icon: Award, permission: 'commissions:read' },
  { path: '/commercial/catalogue', label: 'Catalogue produits', icon: Package, anyPermission: ['catalog:read', 'clients:read'] },
  { path: '/commercial/devis', label: 'Devis', icon: FileText, permission: 'devis:read' },
  // Flux d'activité déplacé dans le menu Gestion.
  { path: '/etudes-comparatives', label: 'Études comparatives', icon: FileBarChart2, permission: 'etudes:read' },
  { path: '/formation', label: 'Formation', icon: GraduationCap, permission: 'formation:read' },
  { path: '/parametres', label: 'Paramètres', icon: Settings },
];

// Les réglages ne sont plus listés dans la barre latérale : « Paramètres »
// ouvre directement la page Vue d'ensemble (hub), qui porte la navigation
// groupée par domaine. Cf. SettingsPage.jsx (OVERVIEW_GROUPS).

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Actif pour un sous-lien : si le lien cible un onglet (?tab=), on compare l'onglet
  // courant (et bare /facturation == onglet 'worksheet'/impression). Sinon match exact.
  const isSubItemActive = (item) => {
    if (item.tab) {
      if (location.pathname !== '/facturation') return false;
      const current = new URLSearchParams(location.search).get('tab');
      return current === item.tab || (!current && item.tab === 'worksheet');
    }
    return location.pathname === item.path;
  };
  const { user, logout, hasRole, hasPermission, isOwner } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeSubmenu, setActiveSubmenu] = useState(null);
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [portalUnreadCount, setPortalUnreadCount] = useState(0);

  const isCommercialRole = user?.role === 'commercial';
  // Le menu Commerce est piloté par la PERMISSION, pas seulement par le rôle : dès
  // qu'un utilisateur reçoit le droit commercial (permission `commercial:read`,
  // p. ex. via des permissions personnalisées), il peut accéder au menu Commerce.
  // Un technicien / user sans ce droit reste sur Gestion (comportement inchangé).
  const hasCommercialAccess = hasPermission('commercial:read');
  // Admin et manager peuvent basculer Gestion ↔ Commerce ; toute personne dotée de
  // l'accès commercial (sans être « commercial » pur, forcé en Commerce) le peut aussi.
  const canToggleMode = user?.role === 'admin'
    || user?.role === 'manager'
    || (hasCommercialAccess && !isCommercialRole);
  const [sidebarMode, setSidebarModeState] = useState(
    () => localStorage.getItem('sidebarMode') || 'gestion'
  );

  useEffect(() => {
    if (isCommercialRole) setSidebarModeState('commerce');
    else if (!canToggleMode) setSidebarModeState('gestion'); // back-office, technicien, etc. → always gestion
  }, [isCommercialRole, canToggleMode]);

  const setSidebarMode = (mode) => {
    if (!canToggleMode) return;
    setSidebarModeState(mode);
    localStorage.setItem('sidebarMode', mode);
  };

  const access = { hasRole, hasPermission, isOwner };
  const menuItems = (isCommercialRole || (canToggleMode && sidebarMode === 'commerce')) ? menuItemsCommerce : menuItemsGestion;

  useEffect(() => {
    settingsAPI.getPublicBranding()
      .then(res => {
        if (res.data?.logo_url) {
          setCompanyLogoUrl(`${process.env.REACT_APP_BACKEND_URL}${res.data.logo_url}`);
        }
      })
      .catch(() => {});
  }, []);

  // Poll the portal-client unread ticket count every 30s and surface it as a
  // red dot on the "Tickets" nav item. Fails silently on error.
  useEffect(() => {
    let cancelled = false;
    const fetchCount = () => {
      ticketsAPI.getPortalUnreadCount()
        .then(res => { if (!cancelled) setPortalUnreadCount(res.data?.count || 0); })
        .catch(() => {});
    };
    fetchCount();
    const id = setInterval(fetchCount, 30000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);
  
  // Check if current path matches a menu item or its submenu
  const isItemActive = (item) => {
    if (item.path) {
      return location.pathname.startsWith(item.path);
    }
    if (item.submenu) {
      // On ignore la query (?tab=) et les items « heading » (sans path) pour la détection.
      return item.submenu.items?.some(sub => sub.path && location.pathname.startsWith(sub.path.split('?')[0])) ||
             item.submenu.quickActions?.some(qa => qa.path && location.pathname.startsWith(qa.path));
    }
    return false;
  };

  // Close submenu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Don't close if clicking on sidebar or submenu panel
      if (e.target.closest('.sidebar') || e.target.closest('.submenu-panel')) {
        return;
      }
      setActiveSubmenu(null);
    };
    
    // Small delay to prevent immediate close on menu item click
    const timer = setTimeout(() => {
      document.addEventListener('click', handleClickOutside);
    }, 100);
    
    return () => {
      clearTimeout(timer);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [activeSubmenu]);


  const handleMenuClick = (item, e) => {
    if (item.submenu) {
      e.preventDefault();
      setActiveSubmenu(activeSubmenu === item.id ? null : item.id);
    } else {
      setActiveSubmenu(null);
      setMobileOpen(false);
    }
  };

  const handleSubmenuItemClick = () => {
    setActiveSubmenu(null);
    setMobileOpen(false);
  };

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="h-16 flex items-center justify-center px-4 border-b border-white/10">
        {!collapsed ? (
          <img
            src={companyLogoUrl || LOGO_URL}
            alt="LDWS Groupe"
            className="h-10 object-contain brightness-0 invert"
          />
        ) : (
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
            <span className="text-white font-bold text-xs">LG</span>
          </div>
        )}
      </div>

      {/* Mode toggle — Commerce / Gestion (admin, manager, ou droit commercial) */}
      {canToggleMode && !isCommercialRole && !collapsed && (
        <div className="px-2 py-2 border-b border-white/10">
          <div className="flex rounded-lg bg-white/10 p-0.5">
            <button
              onClick={() => setSidebarMode('gestion')}
              className={cn(
                "flex-1 text-xs py-1.5 rounded-md font-medium transition-all",
                sidebarMode === 'gestion'
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-white/70 hover:text-white"
              )}
            >
              Gestion
            </button>
            <button
              onClick={() => setSidebarMode('commerce')}
              className={cn(
                "flex-1 text-xs py-1.5 rounded-md font-medium transition-all",
                sidebarMode === 'commerce'
                  ? "bg-white text-slate-900 shadow-sm"
                  : "text-white/70 hover:text-white"
              )}
            >
              Commerce
            </button>
          </div>
        </div>
      )}

      {/* Navigation */}
      <nav className="flex-1 py-3 px-2 space-y-0.5 overflow-y-auto">
        {menuItems.filter((item) => {
          // Masque les entrées dont l'utilisateur n'a pas la permission/le rôle (évite
          // les liens qui aboutiraient à un 403), et les groupes sans aucun élément accessible.
          // Sans contrainte → toujours visible.
          return isMenuItemVisible(item, access);
        }).map((item) => {
          const Icon = item.icon;
          const isActive = isItemActive(item);
          const hasSubmenu = !!item.submenu;
          const isSubmenuOpen = activeSubmenu === item.id;

          if (item.path) {
            const showTicketsDot = item.path === '/tickets' && portalUnreadCount > 0;
            return (
              <NavLink
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace(/\//g, '-').slice(1)}`}
                className={cn(
                  'sidebar-nav-item',
                  isActive && 'active',
                  collapsed && 'justify-center px-2'
                )}
                onClick={() => {
                  setActiveSubmenu(null);
                  setMobileOpen(false);
                }}
              >
                <span className="relative flex-shrink-0">
                  <Icon className="w-4 h-4" />
                  {showTicketsDot && (
                    <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full ring-2 ring-[--sidebar-bg,theme(colors.slate.900)]" />
                  )}
                </span>
                {!collapsed && (
                  <span className="text-[12px] flex-1">
                    {item.label}
                    {showTicketsDot && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold bg-red-500 text-white rounded-full">
                        {portalUnreadCount}
                      </span>
                    )}
                  </span>
                )}
              </NavLink>
            );
          }

          return (
            <button
              key={item.id}
              onClick={(e) => handleMenuClick(item, e)}
              className={cn(
                'sidebar-nav-item w-full',
                (isActive || isSubmenuOpen) && 'active',
                collapsed && 'justify-center px-2'
              )}
              data-testid={`nav-${item.id}`}
            >
              <Icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && (
                <>
                  <span className="text-[12px] flex-1 text-left">{item.label}</span>
                  <ChevronRight className={cn(
                    "w-3 h-3 transition-transform",
                    isSubmenuOpen && "rotate-90"
                  )} />
                </>
              )}
            </button>
          );
        })}

        {/* Separator */}
        {sidebarMode === 'gestion' && (
          <div className="my-2 border-t border-white/10" />
        )}

        {/* Settings — visible par TOUS (chacun peut au moins choisir son thème
            dans « Apparence » ; la page masque le contenu réservé aux admins).
            Lien direct vers la page hub, pas de sous-menu déroulant. */}
        {sidebarMode === 'gestion' && (
          <NavLink
            to="/parametres"
            className={cn(
              'sidebar-nav-item w-full',
              (location.pathname.startsWith('/parametres') || location.pathname.startsWith('/admin')) && 'active',
              collapsed && 'justify-center px-2'
            )}
            data-testid="nav-parametres"
            onClick={() => {
              setActiveSubmenu(null);
              setMobileOpen(false);
            }}
          >
            <Settings className="w-4 h-4 flex-shrink-0" />
            {!collapsed && (
              <span className="text-[12px] flex-1 text-left">Paramètres</span>
            )}
          </NavLink>
        )}
      </nav>

      {/* User section */}
      <div className="p-2 border-t border-white/10">
        {!collapsed ? (
          <div className="flex items-center gap-2 px-2 py-1.5">
            <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-medium flex-shrink-0">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium text-white truncate">
                {user?.first_name} {user?.last_name}
              </p>
            </div>
            <div className="flex-shrink-0">
              <NotificationCenter />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10 flex-shrink-0"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-1.5">
            <div className="w-7 h-7 rounded-full bg-primary text-white flex items-center justify-center text-[10px] font-medium">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <NotificationCenter />
            <Button
              variant="ghost"
              size="icon"
              onClick={logout}
              data-testid="logout-btn"
              className="h-7 w-7 text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}
      </div>

      {/* Collapse toggle */}
      <div className="hidden lg:block p-2 border-t border-white/10">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setCollapsed(!collapsed)}
          className="w-full justify-center text-white/70 hover:text-white hover:bg-white/10 h-7 text-xs"
          data-testid="sidebar-toggle"
        >
          {collapsed ? (
            <ChevronRight className="w-3.5 h-3.5" />
          ) : (
            <>
              <ChevronLeft className="w-3.5 h-3.5 mr-1.5" />
              <span>Réduire</span>
            </>
          )}
        </Button>
      </div>
    </>
  );

  // Find active submenu data (facturation, etc. — les réglages n'ont plus de
  // sous-menu : « Paramètres » est un lien direct vers la page hub).
  const getSubmenuData = () => {
    const item = menuItems.find(m => m.id === activeSubmenu);
    return item?.submenu || null;
  };

  const submenuData = getSubmenuData();

  return (
    <>
      {/* Mobile menu button */}
      <Button
        variant="ghost"
        size="icon"
        className="fixed top-2 left-2 z-50 lg:hidden bg-[var(--sidebar-bg)] text-white hover:bg-[var(--sidebar-bg-end)] h-8 w-8"
        onClick={() => setMobileOpen(!mobileOpen)}
        data-testid="mobile-menu-btn"
      >
        {mobileOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
      </Button>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => {
            setMobileOpen(false);
            setActiveSubmenu(null);
          }}
        />
      )}

      {/* Sidebar */}
      <aside
        className={cn(
          'sidebar flex flex-col',
          collapsed && 'collapsed',
          mobileOpen && 'mobile-open'
        )}
      >
        <NavContent />
      </aside>

      {/* Submenu Panel - Desktop only */}
      {submenuData && (
        <div
          className="submenu-panel hidden lg:flex fixed top-0 z-30 h-full bg-white border-r border-gray-200 shadow-lg flex-col"
          style={{
            left: collapsed ? '60px' : '220px',
            width: '260px',
          }}
        >
          {/* Submenu header */}
          <div className="h-16 flex items-center justify-between px-4 border-b border-gray-100">
            <h3 className="font-semibold text-gray-900 text-sm">{submenuData.title}</h3>
            <button 
              onClick={() => setActiveSubmenu(null)}
              className="text-gray-400 hover:text-gray-600 h-10 w-10 min-h-[44px] min-w-[44px] flex items-center justify-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Quick actions */}
          {submenuData.quickActions && submenuData.quickActions.length > 0 && (
            <div className="p-3 space-y-1.5 border-b border-gray-100">
              {submenuData.quickActions.map((action) => (
                <Button
                  key={action.path}
                  variant="outline"
                  size="sm"
                  className="w-full justify-start text-xs h-11 min-h-[44px] border-primary/30 text-primary hover:bg-primary/5"
                  onClick={() => {
                    navigate(action.path);
                    handleSubmenuItemClick();
                  }}
                >
                  <action.icon className="w-3.5 h-3.5 mr-2" />
                  {action.label}
                </Button>
              ))}
            </div>
          )}

          {/* Submenu items */}
          <div className="flex-1 overflow-y-auto py-2 px-2">
            {(submenuData.items || []).filter((item) => isEntryVisible(item, access)).map((item, idx) => {
              // En-tête de section (non cliquable)
              if (item.heading) {
                return (
                  <div key={`h-${idx}`} className="px-3 pt-3 pb-1 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                    {item.heading}
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = isSubItemActive(item);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={handleSubmenuItemClick}
                  className={cn(
                    "flex items-center gap-2.5 px-3 py-3 rounded-md text-[12px] transition-colors min-h-[44px]",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                  )}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="whitespace-nowrap">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Submenu - Full screen overlay */}
      {submenuData && mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-white flex flex-col">
          {/* Mobile submenu header */}
          <div className="h-14 flex items-center justify-between px-4 border-b border-gray-200 bg-gray-50">
            <button 
              onClick={() => setActiveSubmenu(null)}
              className="flex items-center gap-2 text-gray-600 h-11 min-h-[44px] px-2"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Retour</span>
            </button>
            <h3 className="font-semibold text-gray-900">{submenuData.title}</h3>
            <button 
              onClick={() => {
                setActiveSubmenu(null);
                setMobileOpen(false);
              }}
              className="h-11 w-11 min-h-[44px] min-w-[44px] flex items-center justify-center text-gray-500"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Mobile submenu items */}
          <div className="flex-1 overflow-y-auto py-2 px-3">
            {(submenuData.items || []).filter((item) => isEntryVisible(item, access)).map((item, idx) => {
              if (item.heading) {
                return (
                  <div key={`h-${idx}`} className="px-4 pt-4 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
                    {item.heading}
                  </div>
                );
              }
              const Icon = item.icon;
              const isActive = isSubItemActive(item);
              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => {
                    handleSubmenuItemClick();
                    setMobileOpen(false);
                  }}
                  className={cn(
                    "flex items-center gap-3 px-4 py-4 rounded-lg text-base transition-colors min-h-[52px]",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-gray-700 hover:bg-gray-50"
                  )}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  <span className="whitespace-nowrap">{item.label}</span>
                </NavLink>
              );
            })}
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation Bar - App-like experience */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-white border-t border-gray-200 safe-area-inset-bottom">
        <div className="flex items-center justify-around h-16 px-2">
          {/* Dashboard */}
          <NavLink
            to="/dashboard"
            className={({ isActive }) => cn(
              "flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-colors",
              isActive ? "text-primary bg-primary/10" : "text-gray-500 hover:text-gray-700"
            )}
          >
            <LayoutDashboard className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">Accueil</span>
          </NavLink>

          {/* Clients */}
          <NavLink
            to="/clients"
            className={({ isActive }) => cn(
              "flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-colors",
              isActive ? "text-primary bg-primary/10" : "text-gray-500 hover:text-gray-700"
            )}
          >
            <Users className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">Clients</span>
          </NavLink>

          {/* Tickets */}
          <NavLink
            to="/tickets"
            className={({ isActive }) => cn(
              "flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-colors relative",
              isActive ? "text-primary bg-primary/10" : "text-gray-500 hover:text-gray-700"
            )}
          >
            <span className="relative">
              <Ticket className="w-5 h-5 mb-1" />
              {portalUnreadCount > 0 && (
                <span className="absolute -top-1 -right-2 min-w-[1rem] h-4 px-1 text-[9px] font-semibold bg-red-500 text-white rounded-full flex items-center justify-center">
                  {portalUnreadCount > 9 ? '9+' : portalUnreadCount}
                </span>
              )}
            </span>
            <span className="text-[10px] font-medium">Tickets</span>
          </NavLink>

          {/* Equipements */}
          <NavLink
            to="/equipements"
            className={({ isActive }) => cn(
              "flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-colors",
              isActive ? "text-primary bg-primary/10" : "text-gray-500 hover:text-gray-700"
            )}
          >
            <Printer className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">Équip.</span>
          </NavLink>

          {/* More - Opens full menu */}
          <button
            onClick={() => setMobileOpen(true)}
            className="flex flex-col items-center justify-center w-16 h-14 rounded-lg transition-colors text-gray-500 hover:text-gray-700"
          >
            <Menu className="w-5 h-5 mb-1" />
            <span className="text-[10px] font-medium">Plus</span>
          </button>
        </div>
      </nav>
    </>
  );
};

export default Sidebar;
